import * as THREE from "three";

/**
 * Trauma screen shake (art_review/vfx.md F1), after Eiserloh's "Juicing your cameras": hits ADD trauma (clamped to 1),
 * trauma decays linearly, and the shake is trauma² so small hits barely register while big ones slam. The offset is
 * smooth noise sampled at `Freq` Hz — a camera that moves, not white-noise jitter — plus a little roll.
 *
 * `Scale` is a fraction of the camera's distance to its look point, so the shake is the same share of the screen at
 * every pinch zoom (the review's "× zoom"; read literally against IsoCamera's 0.24–1 zoom multiplier it would be a
 * third of a pixel). At the default zoom, full trauma peaks near 11 % of the screen height; the budgets below keep
 * the battle well under that: base strike 0.45 → ~2 %, boss death 0.7 → ~5 %.
 */
export const Trauma = {
  Decay: 1.8,
  Scale: 0.06,
  RollDeg: 0.6,
  Freq: 25,
  /** Per-effect budgets (the VFX module adds these through `kick`; the camera's own boss shake uses BossSpawn). */
  Budget: { archer: 0, frost: 0, poison: 0, cannon: 0.12, mortar: 0.25, baseStrike: 0.45, bossSpawn: 0.6, bossDeath: 0.7, towerPlace: 0.12 },
  add(trauma: number, amount: number): number { return Math.min(1, Math.max(0, trauma) + Math.max(0, amount)); },
  decay(trauma: number, dt: number): number { return Math.max(0, trauma - Trauma.Decay * dt); },
  /** How much of the maximum shake a trauma level produces. */
  amount(trauma: number): number { return trauma * trauma; },
  /** Smooth 1-D value noise in [-1, 1]: smoothstep between hashed integer lattice values; `seed` decorrelates axes. */
  noise(x: number, seed: number): number {
    const i = Math.floor(x), f = x - i, u = f * f * (3 - 2 * f);
    const h = (n: number) => { const v = Math.sin(n * 127.1 + seed * 311.7) * 43758.5453; return (v - Math.floor(v)) * 2 - 1; };
    return h(i) + (h(i + 1) - h(i)) * u;
  },
  /** Camera-space offset (world units right/up at this camera distance) and roll (radians) for a trauma at time t. */
  offset(trauma: number, t: number, distance: number, out: { x: number; y: number; roll: number }): { x: number; y: number; roll: number } {
    const k = Trauma.amount(trauma), s = t * Trauma.Freq;
    out.x = k * Trauma.Scale * distance * Trauma.noise(s, 1);
    out.y = k * Trauma.Scale * distance * Trauma.noise(s, 2);
    out.roll = k * THREE.MathUtils.degToRad(Trauma.RollDeg) * Trauma.noise(s, 3);
    return out;
  },
};

/** The level camera's shake state (isoCamera.ts): the trauma kicks add to, and its clock. */
export class CameraShake {
  private trauma = 0;
  private clock = 0;
  private shaking = false;
  private readonly out = { x: 0, y: 0, roll: 0 };

  /** Adds trauma (0..1, clamped); see `Trauma`. */
  add(amount: number): void { this.trauma = Trauma.add(this.trauma, amount); }
  get current(): number { return this.trauma; }
  /** Trauma is left, or the last frame was shaken (the camera must go back to rest once more). */
  get active(): boolean { return this.trauma > 0 || this.shaking; }

  /**
   * One frame: back to the rest pose (`rest` places the camera), then offset along the camera's own right/up and roll
   * about its view axis, at `distance` from the look point. The rest pose is recomputed every frame, so the shake never
   * accumulates into the pan.
   */
  step(dt: number, camera: THREE.Camera, distance: number, rest: () => void): void {
    this.clock += dt;
    this.trauma = Trauma.decay(this.trauma, dt);
    rest();
    if (this.trauma > 0) {
      const o = Trauma.offset(this.trauma, this.clock, distance, this.out);
      const e = camera.matrixWorld.elements;
      camera.position.x += e[0] * o.x + e[4] * o.y;
      camera.position.y += e[1] * o.x + e[5] * o.y;
      camera.position.z += e[2] * o.x + e[6] * o.y;
      camera.rotateZ(o.roll);
      camera.updateMatrixWorld();
    }
    this.shaking = this.trauma > 0;
  }
}
